/**
 * facets.js — Collection filtering + sorting via the Section Rendering API.
 *
 * Custom elements:
 *   <facet-filters data-section-id data-target>  — wraps the filter <form>. Intercepts
 *       submit / input / active-chip clicks, fetches ?section_id=..., swaps results,
 *       updates history.pushState, restores focus. Full no-JS fallback (plain form submit).
 *   <facet-sort data-section-id data-target>      — wraps the sort <select>; auto-submits.
 *
 * Dependency-free ES module. Loaded by the section via <script src ... type="module">.
 * Progressive enhancement: if the browser lacks customElements / fetch, the plain
 * <form method="get"> and native <select onchange submit> still work.
 */

const YIELD = () => new Promise((r) => setTimeout(r, 0));

function debounce(fn, wait) {
  let t;
  return function (...args) {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

/**
 * Minimal focus trap for the mobile <dialog> drawer. Returns a teardown fn.
 * (Kept local so facets.js stays standalone; the section may also use the
 * global trapFocus, but native <dialog> already traps focus in modal mode.)
 */
function getFocusable(container) {
  return Array.from(
    container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
  ).filter((el) => !el.hasAttribute('disabled') && el.offsetParent !== null);
}

class FacetFilters extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.targetSelector = this.dataset.target || `#ProductGridTarget`;
    this.form = this.querySelector('form');
    this.debouncedApply = debounce(this.applyFromForm.bind(this), 400);

    if (!this.form) return;

    // Enhance: prevent full reload, drive via fetch.
    this.form.addEventListener('submit', this.onSubmit.bind(this));

    // Live filtering when a control changes (checkbox, price range).
    this.form.addEventListener('input', this.onInput.bind(this));
    this.form.addEventListener('change', this.onInput.bind(this));

    // Active-filter chips + "clear all" (delegated; they may live outside the form).
    this.addEventListener('click', this.onClick.bind(this));

    // Back / forward navigation.
    this._onPopState = this.onPopState.bind(this);
    window.addEventListener('popstate', this._onPopState);

    // Mobile drawer <dialog> wiring.
    this.setupDrawer();

    // Desktop dropdown behavior (single-open, click-outside, Escape).
    this.setupDropdowns();

    this.setAttribute('data-enhanced', 'true');
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this._onPopState);
    if (this._onDocClick) document.removeEventListener('click', this._onDocClick);
  }

  /* -------------------------- Dropdowns (desktop) --------------------------- */
  setupDropdowns() {
    this.desktopMq = window.matchMedia('(min-width: 769px)');
    const disclosures = () =>
      Array.from(this.querySelectorAll('.facets__inner .facet-group__disclosure'));

    // Opening one dropdown closes the others (desktop only). `toggle` doesn't
    // bubble, so listen in the capture phase — this survives innerHTML swaps.
    this.addEventListener(
      'toggle',
      (e) => {
        const d = e.target;
        if (!d.matches || !d.matches('.facet-group__disclosure') || !d.open) return;
        if (!this.desktopMq.matches) return;
        disclosures().forEach((other) => {
          if (other !== d) other.open = false;
        });
      },
      true
    );

    // Clicking anywhere outside an open dropdown closes it (desktop only).
    this._onDocClick = (e) => {
      if (!this.desktopMq.matches) return;
      if (e.target.closest('.facet-group__disclosure')) return;
      disclosures().forEach((d) => {
        d.open = false;
      });
    };
    document.addEventListener('click', this._onDocClick);

    // Escape closes the open dropdown and returns focus to its trigger.
    this.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || !this.desktopMq.matches) return;
      disclosures().forEach((d) => {
        if (d.open) {
          d.open = false;
          const summary = d.querySelector('summary');
          if (summary) summary.focus();
        }
      });
    });
  }

  /* ----------------------------- Drawer (mobile) ---------------------------- */
  setupDrawer() {
    this.dialog = this.querySelector('dialog.facets__drawer');
    const openBtn = document.querySelector(`[data-facets-open="${this.sectionId}"]`);
    if (this.dialog && openBtn) {
      openBtn.addEventListener('click', () => this.openDrawer());
      this.dialog.querySelectorAll('[data-facets-close]').forEach((btn) =>
        btn.addEventListener('click', () => this.closeDrawer())
      );
    }
  }

  openDrawer() {
    if (!this.dialog) return;
    this.dialog.showModal();
    document.body.style.overflow = 'hidden';
    const focusable = getFocusable(this.dialog);
    if (focusable.length) focusable[0].focus();
  }

  closeDrawer() {
    if (!this.dialog) return;
    this.dialog.close();
    document.body.style.overflow = '';
  }

  /* ------------------------------- Handlers -------------------------------- */
  onSubmit(event) {
    event.preventDefault();
    this.applyFromForm();
    this.closeDrawer();
  }

  onInput(event) {
    // Only react to real filter inputs (skip price typing on every keystroke via debounce).
    const el = event.target;
    if (!el.name) return;
    if (el.type === 'text' || el.type === 'number') {
      this.debouncedApply();
    } else {
      this.applyFromForm();
    }
  }

  onClick(event) {
    const remove = event.target.closest('[data-facet-remove]');
    if (remove) {
      event.preventDefault();
      this.applyFromUrl(remove.getAttribute('href'));
      return;
    }
    const clearAll = event.target.closest('[data-facet-clear-all]');
    if (clearAll) {
      event.preventDefault();
      this.applyFromUrl(clearAll.getAttribute('href'));
    }
  }

  onPopState(event) {
    const url = (event.state && event.state.facetUrl) || window.location.href;
    this.render(url, { pushState: false, focusTarget: null });
  }

  /* ------------------------------- Applying -------------------------------- */
  applyFromForm() {
    const params = new URLSearchParams(new FormData(this.form));
    // Drop empty values so the URL stays clean.
    for (const [key, value] of [...params.entries()]) {
      if (value === '') params.delete(key);
    }
    const url = `${this.form.action.split('?')[0]}?${params.toString()}`;
    this.render(url, { pushState: true, focusTarget: null });
  }

  applyFromUrl(url) {
    this.render(url, { pushState: true, focusTarget: null });
  }

  /* ------------------------------- Rendering ------------------------------- */
  async render(url, { pushState = true } = {}) {
    const target = document.querySelector(this.targetSelector);
    if (!target || !window.fetch) {
      // No-JS-equivalent fallback: hard navigate.
      window.location.assign(url);
      return;
    }

    this.setBusy(true);

    const fetchUrl = new URL(url, window.location.origin);
    fetchUrl.searchParams.set('section_id', this.sectionId);

    try {
      const res = await fetch(fetchUrl.toString());
      if (!res.ok) throw new Error(`Section render failed: ${res.status}`);
      const text = await res.text();
      await YIELD();

      const doc = new DOMParser().parseFromString(text, 'text/html');

      // Swap the results region (product grid + pagination + count).
      const newTarget = doc.querySelector(this.targetSelector);
      if (newTarget) target.innerHTML = newTarget.innerHTML;

      // Swap the filter form controls (counts change as facets narrow).
      const newFilters = doc.querySelector('[data-facet-filters-inner]');
      const curFilters = this.querySelector('[data-facet-filters-inner]');
      if (newFilters && curFilters) {
        // Remember which filter dropdowns were open so selecting a second value
        // (e.g. two sizes) doesn't collapse the panel after every checkbox.
        const openLabels = new Set(
          Array.from(curFilters.querySelectorAll('.facet-group'))
            .filter((g) => {
              const d = g.querySelector('details');
              return d && d.open;
            })
            .map((g) => g.getAttribute('name'))
        );
        curFilters.innerHTML = newFilters.innerHTML;
        curFilters.querySelectorAll('.facet-group').forEach((g) => {
          if (openLabels.has(g.getAttribute('name'))) {
            const d = g.querySelector('details');
            if (d) d.open = true;
          }
        });
      }

      // Swap active-filter chips.
      const newChips = doc.querySelector('[data-active-facets]');
      const curChips = this.querySelector('[data-active-facets]');
      if (newChips && curChips) curChips.innerHTML = newChips.innerHTML;

      // Swap the sort select's selection if present.
      const newSort = doc.querySelector('[data-facet-sort] select');
      const curSort = document.querySelector('[data-facet-sort] select');
      if (newSort && curSort) curSort.value = newSort.value;

      if (pushState) {
        history.pushState({ facetUrl: url }, '', url);
      }

      // Re-init reveal animations for freshly injected cards, if global is present.
      if (window.__aetherInitAnimations) {
        window.__aetherInitAnimations(target);
      }

      // Move focus to the results heading for screen-reader continuity.
      const heading = target.querySelector('[data-results-count]');
      if (heading) {
        heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
      }
    } catch (err) {
      // On failure, fall back to a full navigation so the user is never stuck.
      console.error('[facets]', err);
      window.location.assign(url);
    } finally {
      this.setBusy(false);
    }
  }

  setBusy(state) {
    const target = document.querySelector(this.targetSelector);
    if (target) target.setAttribute('aria-busy', state ? 'true' : 'false');
    this.toggleAttribute('data-loading', state);
  }
}

class FacetSort extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.select = this.querySelector('select');
    if (!this.select) return;

    this.select.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', this.select.value);
      url.searchParams.delete('page');

      const facets = document.querySelector('facet-filters');
      if (facets && facets.render && facets.getAttribute('data-enhanced')) {
        facets.render(url.toString(), { pushState: true });
      } else {
        // No enhanced filters present → submit the wrapping form or navigate.
        const form = this.querySelector('form');
        if (form) form.submit();
        else window.location.assign(url.toString());
      }
    });
  }
}

if ('customElements' in window) {
  if (!customElements.get('facet-filters')) customElements.define('facet-filters', FacetFilters);
  if (!customElements.get('facet-sort')) customElements.define('facet-sort', FacetSort);
}
